import { call, put, take, takeLatest, fork, cancel, select } from 'redux-saga/effects'
import type { Task as SagaTask } from 'redux-saga'
import type { PayloadAction } from '@reduxjs/toolkit'
import { collection, query, where, orderBy, doc, setDoc, deleteDoc, getDocs, Timestamp } from 'firebase/firestore'
import { showToast } from '@/components/app/Toast/showToast'
import { db } from '../../lib/firebase'
import { createSnapshotChannel } from '../utils/createSnapshotChannel'
import type { RootState } from '../index'
import {
    createCommentSaga,
    deleteCommentSaga,
    prefetchCommentsSaga,
    startListening,
    stopListening,
    setCommentsForTask,
    setListening,
    setLoading,
    clearComments,
    type Comment
} from './commentsSlice'

// All of a task's comments, oldest-first (display order). Unbounded: we show every comment, no
// pagination. Removing the old `limit(20)` window fixed comments not appearing after add/delete —
// a `limit` query doesn't reliably latency-compensate local writes under forced long-polling
// (SCD-925). Uses the existing composite index comments(taskId, workspaceId, createdAt).
function commentsQuery(taskId: string, workspaceId: string) {
    return query(
        collection(db, 'comments'),
        where('taskId', '==', taskId),
        where('workspaceId', '==', workspaceId),
        orderBy('createdAt', 'asc')
    )
}

function commentsFromSnapshot(snapshot: any): Comment[] {
    return snapshot.docs.map((d: any) => ({ id: d.id, ...d.data() } as Comment))
}

function* createCommentWorker(action: PayloadAction<{ taskId: string; workspaceId: string; authorId: string; content: unknown }>): Generator<any, void, any> {
    const { taskId, workspaceId, authorId, content } = action.payload

    const cleanContent = JSON.parse(JSON.stringify(content))
    // Concrete client timestamp (not serverTimestamp): a pending serverTimestamp reads as null
    // locally, which would sort the new comment oddly until the server value lands.
    const now = Timestamp.now()
    const ref = doc(collection(db, 'comments'))
    try {
        // onSnapshot reflects this local write immediately (latency compensation) on the unbounded
        // query, so the author sees their own comment without an optimistic store insert (SCD-925).
        yield call(() => setDoc(ref, { taskId, workspaceId, authorId, content: cleanContent, reactions: [], createdAt: now, updatedAt: now }))
        showToast('Comment added')
    } catch (err) {
        console.error('[CommentSaga] Error adding comment:', err)
    }
}

function* deleteCommentWorker(action: PayloadAction<string>): Generator<any, void, any> {
    const commentId = action.payload
    try {
        // The listener reflects the deletion immediately (latency compensation) — no optimistic remove.
        yield call(() => deleteDoc(doc(db, 'comments', commentId)))
        showToast('Comment deleted')
    } catch (err) {
        console.error('Error deleting comment:', err)
    }
}

// One-shot prefetch: warm a task's comments into the store on hover, so opening it shows the
// thread immediately. Skipped if a modal's live listener is active (don't clobber it) or if the
// store already holds this task. Best-effort — the modal's listener loads them anyway.
function* prefetchCommentsWorker(action: PayloadAction<{ taskId: string; workspaceId: string }>): Generator<any, void, any> {
    const { taskId, workspaceId } = action.payload
    const before: { taskId: string | null; listening: boolean } = yield select((s: RootState) => ({
        taskId: s.comments.taskId,
        listening: s.comments.listening
    }))
    if (before.listening || before.taskId === taskId) return

    try {
        const snap = yield call(() => getDocs(commentsQuery(taskId, workspaceId)))
        // A listener may have started (or the same prefetch already landed) while we fetched.
        const after: { taskId: string | null; listening: boolean } = yield select((s: RootState) => ({
            taskId: s.comments.taskId,
            listening: s.comments.listening
        }))
        if (after.listening || after.taskId === taskId) return
        yield put(setCommentsForTask({ taskId, comments: commentsFromSnapshot(snap) }))
    } catch {
        // Best-effort — the modal's own listener still loads the thread on open.
    }
}

// Per-task comment listener — runs only while a task is open (TaskModal). Loads all of the task's
// comments live. Comments are not loaded workspace-wide, keeping them out of the initial Firestore
// wave and memory. Each snapshot full-replaces the store (the query is unbounded, so there are no
// window evictions to confuse with deletes).
function* listenTaskComments(taskId: string, workspaceId: string): Generator<any, void, any> {
    // Reuse a prefetched thread: if the store already holds this task's comments, don't clear
    // (which would flash empty) — just attach the listener and let its first snapshot reconcile.
    const currentTaskId: string | null = yield select((s: RootState) => s.comments.taskId)
    if (currentTaskId !== taskId) {
        yield put(clearComments())
        yield put(setLoading(true))
    }
    yield put(setListening(true))

    const channel = yield call(createSnapshotChannel, commentsQuery(taskId, workspaceId))

    try {
        while (true) {
            const event: any = yield take(channel)
            if (event.type === 'data') {
                yield put(setCommentsForTask({ taskId, comments: commentsFromSnapshot(event.snapshot) }))
            }
            yield put(setLoading(false))
        }
    } finally {
        channel.close()
    }
}

function* commentsSubscriptionWorker(action: PayloadAction<{ taskId: string; workspaceId: string }>): Generator<any, void, any> {
    const { taskId, workspaceId } = action.payload
    const listener: SagaTask = yield fork(listenTaskComments, taskId, workspaceId)
    try {
        yield take(stopListening.type)
    } finally {
        yield cancel(listener)
        yield put(clearComments())
    }
}

export function* watchCommentsSaga() {
    yield takeLatest(createCommentSaga.type, createCommentWorker)
    yield takeLatest(deleteCommentSaga.type, deleteCommentWorker)
    yield takeLatest(prefetchCommentsSaga.type, prefetchCommentsWorker)
    yield takeLatest(startListening.type, commentsSubscriptionWorker)
}
